import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import GoogleIcon from '@mui/icons-material/Google'; // fallback for Google Pay
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'; // fallback for PhonePe
import PaymentIcon from '@mui/icons-material/Payment'; // fallback for Paytm
import { QRCodeSVG } from 'qrcode.react';

const PLANS: Record<
  string,
  { name: string; base: number; gst: number; total: number }
> = {
  'beginner-path': {
    name: 'Beginner Path',
    base: 9999,
    gst: 300,
    total: 10299,
  },
  'growth-accelerator': {
    name: 'Sales Accelerator',
    base: 12999,
    gst: 389,
    total: 13388,
  },
};

const UPI_ID = 'maverick20885@okaxis';
const UPI_NAME = 'Rajiv Williams';
const UPI_NOTE = 'Mentoring Payment';

const getUpiLink = (amount: number) =>
  `upi://pay?pa=${encodeURIComponent(UPI_ID)}&pn=${encodeURIComponent(
    UPI_NAME,
  )}&am=${amount}&cu=INR&tn=${encodeURIComponent(UPI_NOTE)}`;

const PaymentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const plan = id ? PLANS[id] : undefined;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!plan) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: '100%',
            textAlign: 'center',
            p: 3,
            borderRadius: 2,
            bgcolor: 'background.paper',
            boxShadow: 3,
          }}
        >
          <Typography variant="h5" color="error" fontWeight={700}>
            Plan not found
          </Typography>
          <Typography sx={{ mt: 2 }}>
            The selected plan does not exist. Please check your link.
          </Typography>
        </Box>
      </Container>
    );
  }

  const upiLink = getUpiLink(plan.total);

  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        mt: { xs: 4, md: 8 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          p: { xs: 2, sm: 4 },
          borderRadius: 3,
          bgcolor: 'background.paper',
          boxShadow: { xs: 2, sm: 6 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Stack spacing={2} alignItems="center" sx={{ width: '100%', mb: 2 }}>
          <QrCode2RoundedIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
          <Typography
            variant="h5"
            fontWeight={700}
            color="primary.main"
            sx={{ letterSpacing: 1 }}
          >
            Scan to Pay
          </Typography>
        </Stack>
        <Box
          sx={{
            bgcolor: '#f6f8fa',
            p: 2,
            borderRadius: 2,
            mb: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            boxShadow: 1,
          }}
        >
          <QRCodeSVG
            value={upiLink}
            size={isMobile ? 180 : 220}
            level="H"
            includeMargin
            style={{ borderRadius: 8 }}
          />
          {/* <Typography
            variant="subtitle2"
            sx={{
              mt: 1,
              color: "text.secondary",
              fontSize: 13,
              wordBreak: "break-all",
            }}
          >
            UPI ID: <b>{UPI_ID}</b>
          </Typography> */}
        </Box>
        <Box
          sx={{
            width: '100%',
            bgcolor: '#f6f8fa',
            borderRadius: 2,
            p: { xs: 2, sm: 3 },
            mb: 3,
            boxShadow: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ mb: 1, color: 'text.primary' }}
          >
            {plan.name}
          </Typography>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ mb: 0.5 }}
          >
            <Typography color="text.secondary">Total Training Fee</Typography>
            <Typography fontWeight={500}>₹{plan.base}</Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ mb: 0.5 }}
          >
            <Typography color="text.secondary">GST (3%)</Typography>
            <Typography fontWeight={500}>₹{plan.gst}</Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ mt: 1, pt: 1, borderTop: '1px dashed #e0e0e0' }}
          >
            <Typography fontWeight={700} color="primary.main">
              Subtotal
            </Typography>
            <Typography fontWeight={700} color="primary.main">
              ₹{plan.total}
            </Typography>
          </Stack>
        </Box>
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          sx={{
            fontWeight: 700,
            fontSize: 18,
            py: 1.5,
            mb: 2,
            boxShadow: 2,
            borderRadius: 2,
            letterSpacing: 1,
          }}
          href={upiLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          Pay Now
        </Button>
        {isMobile && (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              width: '100%',
              justifyContent: 'center',
              mt: 1,
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              startIcon={<GoogleIcon />}
              sx={{
                fontWeight: 600,
                borderRadius: 2,
                minWidth: 0,
                px: 2,
                fontSize: 14,
              }}
              href={upiLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              G Pay
            </Button>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              startIcon={<PhoneIphoneIcon />}
              sx={{
                fontWeight: 600,
                borderRadius: 2,
                minWidth: 0,
                px: 2,
                fontSize: 14,
              }}
              href={upiLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              PhonePe
            </Button>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              startIcon={<PaymentIcon />}
              sx={{
                fontWeight: 600,
                borderRadius: 2,
                minWidth: 0,
                px: 2,
                fontSize: 14,
              }}
              href={upiLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              Paytm
            </Button>
          </Stack>
        )}
        <Typography
          variant="caption"
          sx={{
            mt: 4,
            color: 'text.disabled',
            textAlign: 'center',
            width: '100%',
            letterSpacing: 0.2,
          }}
        >
          Payments are processed securely. For help, contact support.
        </Typography>
      </Box>
    </Container>
  );
};

export default PaymentPage;
